import React from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { usePlatform } from '~/services/platform';
import { useStore } from '~/stores';
import { useViewportScale } from '~/utils/hooks/useViewportScale';
import { interactiveElementsStore } from '~/stores/interactiveElements.store';
import { useEffectiveFitMode } from '~/utils/hooks/useEffectiveFitMode';
import InteractiveButton from './InteractiveButton';
import InteractiveQRCode from './InteractiveQRCode';
import InteractiveWeather from './InteractiveWeather';
import InteractiveMediaOverlay from './InteractiveMediaOverlay';

// v1.0.131 (Renato 2026-06-02): z-index subido de 9999 → 10500 pra garantir
// que botões interativos NUNCA fiquem cobertos por SecretArea (10000),
// ProgressBar (10000), KioskOverlay (9998), ou TouchInterceptor (9997).
// Renato reportou no SM-X520/Android 16: botão visível mas toque não
// disparava o trigger.
const LayerContainer = styled.div`
  position: fixed;
  z-index: 10500;
  pointer-events: none;
`;

const InteractiveElementLayer: React.FC = observer(() => {
  const platform = usePlatform();
  const { authStore } = useStore();

  // v1.0.190 (Renato 2026-06-14): espelha ScaledViewport via useEffectiveFitMode.
  // Mesma lógica em 1 lugar só: Android+kiosk OFF → contain; resto → ENV_FIT_MODE.
  const effectiveMode = useEffectiveFitMode();
  const { scaleX, scaleY, width, height } = useViewportScale(effectiveMode);

  // Regra de negócio (Renato, 2026-06-05):
  //  - BOTÕES: em telas com TOQUE (tablet) OU Browser (mouse). SÓ NÃO em TV
  //    (Tizen/webOS, não-touch — não há como clicar com controle remoto).
  //    Correção Renato 2026-08-07: antes exigia toque (`hasTouch && !isTV`) e o
  //    botão sumia no Browser desktop — decisão revista, browser (mouse) mostra.
  //  - QR CODE: em TODAS as telas (browser, tablet, TV) — o cliente escaneia.
  //  - CLIMA:   em TODAS as telas (browser, tablet, TV) — display-only.
  const isTV = platform.isTV();
  const buttonsAllowed = !isTV; // botão: touch (tablet) OU mouse (browser); TV nunca

  const { allButtons, allQRCodes, allWeather, allMediaOverlays, playVersion } =
    interactiveElementsStore;
  const buttons = buttonsAllowed ? allButtons : [];
  const qrCodes = allQRCodes; // todas as telas
  const weather = allWeather; // todas as telas
  const mediaOverlays = allMediaOverlays; // todas as telas (display-only)

  if (
    buttons.length === 0 &&
    qrCodes.length === 0 &&
    weather.length === 0 &&
    mediaOverlays.length === 0
  ) {
    return null;
  }

  const deviceId = authStore.deviceId || '';

  const renderedWidth = width * scaleX;
  const renderedHeight = height * scaleY;
  const left = (window.innerWidth - renderedWidth) / 2;
  const top = (window.innerHeight - renderedHeight) / 2;

  return createPortal(
    <LayerContainer
      style={{
        top: `${top}px`,
        left: `${left}px`,
        width: `${renderedWidth}px`,
        height: `${renderedHeight}px`,
      }}
    >
      {/* Conteúdo sobreposto primeiro = pintado no fundo da camada (sob
          QR/clima/botões), mas ainda acima do conteúdo principal. */}
      {mediaOverlays.map((el) => (
        <InteractiveMediaOverlay key={`${el.id}-${playVersion}`} element={el} />
      ))}
      {qrCodes.map((el) => (
        <InteractiveQRCode
          key={`${el.id}-${playVersion}`}
          element={el}
          deviceId={deviceId}
        />
      ))}
      {weather.map((el) => (
        <InteractiveWeather key={`${el.id}-${playVersion}`} element={el} />
      ))}
      {buttons.map((el) => (
        <InteractiveButton key={`${el.id}-${playVersion}`} element={el} />
      ))}
    </LayerContainer>,
    document.body,
  );
});

export default InteractiveElementLayer;
